/** Pengiriman uniform per jenis shader untuk satu sprite (dipisah dari orb-webgl-draw.ts agar berkas tetap kecil). */

import {
	MORPH_LIGHT_TINT_ALPHA,
	MORPH_LIGHT_TINT_RGB,
	morphFrameState,
	morphRotation,
	morphSteps,
} from "./orb-morph-motion";
import { rgbToUnit } from "./orb-webgl-palette";
import type { GlassLocations, MorphLocations } from "./orb-webgl-programs";
import type {
	LiquidGlassInstanceState,
	LiquidMorphInstanceState,
} from "./orb-webgl-stage.types";

/** Geometri sprite dalam piksel PERANGKAT: origin viewport (kiri-bawah GL) dan sisi. */
export interface SpriteGeometry {
	x: number;
	y: number;
	width: number;
	height: number;
}

export function setGlassUniforms(
	gl: WebGL2RenderingContext,
	u: GlassLocations,
	state: LiquidGlassInstanceState,
	geo: SpriteGeometry,
	elapsedMs: number,
	still: boolean,
): void {
	gl.uniform2f(u.uResolution, geo.width, geo.height);
	gl.uniform2f(u.uViewportOrigin, geo.x, geo.y);
	gl.uniform1f(u.uTime, still ? 0 : elapsedMs / 1000);
	gl.uniform1f(u.uDistortion, state.uniforms.distortion);
	gl.uniform1f(u.uSpeed, state.uniforms.speed);
	gl.uniform1f(u.uFlow, state.uniforms.flow);
	gl.uniform1f(u.uSparkle, state.uniforms.sparkle);
	// uniform3f (bukan uniform3fv) — rgbToUnit mengembalikan tuple readonly,
	// dan ini menghindari alokasi array sementara tiap frame untuk hal sekecil ini.
	const [c0x, c0y, c0z] = rgbToUnit(state.colorStops[0]);
	const [c1x, c1y, c1z] = rgbToUnit(state.colorStops[1]);
	const [c2x, c2y, c2z] = rgbToUnit(state.colorStops[2]);
	const [c3x, c3y, c3z] = rgbToUnit(state.colorStops[3]);
	gl.uniform3f(u.uColor0, c0x, c0y, c0z);
	gl.uniform3f(u.uColor1, c1x, c1y, c1z);
	gl.uniform3f(u.uColor2, c2x, c2y, c2z);
	gl.uniform3f(u.uColor3, c3x, c3y, c3z);
	gl.uniform1f(u.uBrightness, state.brightness);
}

export function setMorphUniforms(
	gl: WebGL2RenderingContext,
	u: MorphLocations,
	state: LiquidMorphInstanceState,
	geo: SpriteGeometry,
	elapsedMs: number,
	still: boolean,
): void {
	// Beku/reduced-motion → frame TENANG (bola), bukan pose kusut yang membeku.
	const frame = morphFrameState(elapsedMs / 1000, still);
	gl.uniform2f(u.uResolution, geo.width, geo.height);
	gl.uniform2f(u.uViewportOrigin, geo.x, geo.y);
	gl.uniform1f(u.uTime, frame.time);
	gl.uniform1f(u.uK, frame.k);
	gl.uniform1f(u.uTaper, frame.taper);
	gl.uniformMatrix3fv(u.uRot, false, morphRotation(frame.time));
	gl.uniform1i(u.uSteps, morphSteps(geo.width));
	gl.uniform1i(u.uMode, state.light ? 1 : 0);
	gl.uniform1f(u.uBrightness, state.brightness);
	const [tr, tg, tb] = MORPH_LIGHT_TINT_RGB;
	gl.uniform3f(u.uLightTint, tr, tg, tb);
	gl.uniform1f(u.uLightTintA, MORPH_LIGHT_TINT_ALPHA);
}
